import { styleguideColors, styleguideFontSize } from '@/Client/Styles/kaiShikiTokens';
import { present } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';
import { useRef } from 'react';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { pxToRem } from '../../Styles/pxToRem';
import { LabeledInputSize, LabeledInputStyle } from './LabeledInputCommonTypes';

const LABELED_INPUT_CONTAINER_STYLE = css`
  label: LABELED_INPUT_CONTAINER_STYLE;
  display: flex;
  align-items: center;
  position: relative;
  padding-top: ${LAYOUT2.spacingHalf}px;
  ${TYPOGRAPHY2.xSmall}
  background-color: inherit;
`;

const LABELED_INPUT_CONTAINER_STYLE_TINY = css`
  label: LABELED_INPUT_CONTAINER_STYLE_TINY;
  position: relative;
  display: flex;
  align-items: center;
  min-height: 44px;
  ${TYPOGRAPHY2.xSmall}
`;

const LABELED_INPUT_CONTAINER_SIZE_STYLES = {
  tiny: LABELED_INPUT_CONTAINER_STYLE_TINY,
  small: LABELED_INPUT_CONTAINER_STYLE,
  tightSmall: LABELED_INPUT_CONTAINER_STYLE,
  medium: LABELED_INPUT_CONTAINER_STYLE,
  tightMedium: LABELED_INPUT_CONTAINER_STYLE,
  large: LABELED_INPUT_CONTAINER_STYLE,
};

const LABELED_INPUT_CONTAINER_NO_TOP_LABEL_STYLE = css`
  label: LABELED_INPUT_CONTAINER_NO_TOP_LABEL_STYLE;
  padding-top: 0;
`;

const COMMON_LABEL_STYLE = css`
  label: LABEL_STYLE;
  position: absolute;
  top: -2.5px;
  left: ${LAYOUT2.spacing1p5}px;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
  background-color: inherit;
  padding: 0 ${LAYOUT2.spacingP5}px;
  z-index: 1;
  line-height: 1.2;
`;

const LABEL_STYLE_SMALL = css`
  label: LABEL_STYLE_SMALL;
  top: 0px;
  margin-left: -4px;
  ${TYPOGRAPHY2.tiny}
`;

const LABEL_STYLE_CALCULATED = css`
  label: LABEL_STYLE_CALCULATED;
  color: var(--text);
  background-color: inherit;
  color: var(--text2);
  top: 9px;
  left: 0;
  width: 100%;
  text-align: center;
`;

const LABEL_STYLE_ATTENTION = css`
  label: LABEL_STYLE_ATTENTION;
  color: var(--text);
  background-color: var(--backgroundYellow2);
  border: none;
`;

const LABEL_STYLES = {
  normal: css``,
  calculated: LABEL_STYLE_CALCULATED,
  attention: LABEL_STYLE_ATTENTION,
  taskCardField: css``,
};

const LABEL_SIZE_STYLES = {
  tiny: css``,
  small: LABEL_STYLE_SMALL,
  tightSmall: LABEL_STYLE_SMALL,
  medium: css``,
  tightMedium: css``,
  large: css``,
};

const INPUT_CONTAINER_STYLE = css`
  label: INPUT_CONTAINER_STYLE;
  position: relative;
  transition: border-color 0.2s ease;
  border: 1px solid var(--border2);
  min-height: 44px;
  display: flex;
  flex-grow: 1;
  align-items: center;
`;

const INPUT_CONTAINER_STYLE_CALCULATE = css`
  label: INPUT_CONTAINER_STYLE_CALCULATE;
  background: var(--background3);
  border-left: 1px solid transparent;
  border-top: 1px solid transparent;
  border-right: 1px solid transparent;
  border-bottom: 1px solid var(--border3);
`;

const INPUT_CONTAINER_STYLE_ATTENTION = css`
  label: INPUT_CONTAINER_STYLE_ATTENTION;
  background: var(--backgroundYellow2);
  border: 1px solid transparent;
`;

const INPUT_CONTAINER_STYLE_TASK_CARD_FIELD = css`
  label: INPUT_CONTAINER_STYLE_TASK_CARD_FIELD;
  border: 1px solid var(--border2);
  min-height: 35px;
  max-height: 35px;
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyInput)};
  font-weight: 400;
  color: ${styleguideColors.ink};
  transition: border-color 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    border-color: var(--border);
  }
`;

const INPUT_CONTAINER_STYLE_DISABLED = css`
  label: INPUT_CONTAINER_STYLE_DISABLED;
  opacity: 0.67;
  font-style: italic;
  cursor: default;
`;

const INPUT_CONTAINER_STYLES = {
  normal: css``,
  calculated: INPUT_CONTAINER_STYLE_CALCULATE,
  attention: INPUT_CONTAINER_STYLE_ATTENTION,
  taskCardField: INPUT_CONTAINER_STYLE_TASK_CARD_FIELD,
};

const INPUT_CONTAINER_SIZE_STYLES = {
  tiny: css`
    label: INPUT_CONTAINER_STYLE_SIZES_TINY;
    border: 1px solid transparent;
  `,
  small: css`
    label: INPUT_CONTAINER_STYLE_SIZES_SMALL;
    padding-left: ${LAYOUT2.spacing1p5}px;
    padding-right: ${LAYOUT2.spacing1p5}px;
  `,
  tightSmall: css`
    label: INPUT_CONTAINER_STYLE_SIZES_TIGHT_SMALL;
    padding-left: ${LAYOUT2.spacing1p5}px;
    padding-right: ${LAYOUT2.spacing1p5}px;
  `,
  medium: css`
    label: INPUT_CONTAINER_STYLE_SIZES_MEDIUM;
    padding-left: ${LAYOUT2.spacing2}px;
    padding-right: ${LAYOUT2.spacing2}px;
    gap: ${LAYOUT2.spacing1}px;
  `,
  tightMedium: css`
    label: INPUT_CONTAINER_STYLE_SIZES_MEDIUM;
    padding-left: ${LAYOUT2.spacing1}px;
    padding-right: ${LAYOUT2.spacing1}px;
    gap: ${LAYOUT2.spacing1}px;
  `,
  large: css`
    label: INPUT_CONTAINER_STYLE_SIZES_LARGE;
    padding-left: ${LAYOUT2.spacing2}px;
    padding-right: ${LAYOUT2.spacing2}px;
    gap: ${LAYOUT2.spacing2}px;
  `,
};

const INPUT_CONTAINER_STYLE_FOCUSED = css`
  label: INPUT_CONTAINER_STYLE_FOCUSED;
  border: 1px solid var(--border);
`;

/**
 * A focused task-card field wears a 2px ink frame without its box shifting: its own 1px border,
 * plus a 1px ring drawn by a pseudo-element just inside it. The ring is its own layer, painted
 * after every child, so it sits over a stepper's +/− column rather than under it.
 */
const INPUT_CONTAINER_STYLE_FOCUSED_TASK_CARD_FIELD = css`
  label: INPUT_CONTAINER_STYLE_FOCUSED_TASK_CARD_FIELD;
  border-color: ${styleguideColors.ink};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid ${styleguideColors.ink};
    pointer-events: none;
  }
`;

const INPUT_CONTAINER_STYLE_TASK_CARD_FIELD_SIZE_OVERRIDE = css`
  label: INPUT_CONTAINER_STYLE_TASK_CARD_FIELD_SIZE_OVERRIDE;
  padding-left: 10px;
  padding-right: 10px;
  gap: 0;
`;

const INPUT_CONTAINER_STYLE_FOCUSED_SIZES = {
  tiny: css`
    label: INPUT_CONTAINER_STYLE_FOCUSED_TINY;
    border: 1px solid var(--border);
  `,
  small: INPUT_CONTAINER_STYLE_FOCUSED,
  tightSmall: INPUT_CONTAINER_STYLE_FOCUSED,
  medium: INPUT_CONTAINER_STYLE_FOCUSED,
  tightMedium: INPUT_CONTAINER_STYLE_FOCUSED,
  large: INPUT_CONTAINER_STYLE_FOCUSED,
};

const UNIT_BEFORE_STYLE = css`
  label: UNIT_BEFORE_STYLE;
  padding-left: ${LAYOUT2.spacing1}px;
  padding-right: 0;
`;

const UNIT_BEFORE_STYLE_TIGHT_SMALL = css`
  label: UNIT_BEFORE_STYLE_TIGHT_SMALL;
  padding-left: ${LAYOUT2.spacingP5}px;
  padding-right: 0;
`;

const CLICKABLE_CONTAINER_STYLE = css`
  label: CLICKABLE_CONTAINER_STYLE;
  cursor: pointer;
`;

const LABEL_CONTENT_STYLE = css`
  label: LABEL_CONTENT_STYLE;
  display: inline-flex;
  align-items: baseline;
  gap: ${LAYOUT2.spacingP5}px;
`;

const OPTIONAL_INDICATOR_STYLE = css`
  label: OPTIONAL_INDICATOR_STYLE;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text2);
`;

export const LabeledInputPresenter = ({
  children,
  width,
  style = 'normal',
  size = 'medium',
  backgroundColor,
  onClick,
  title,
  isFocused,
  label,
  unitBefore,
  disabled = false,
  optional = false,
  frameColor,
}: {
  children: React.ReactNode;
  width: string | undefined;
  style: LabeledInputStyle;
  size: LabeledInputSize;
  backgroundColor?: string;
  onClick: () => void;
  title: string;
  isFocused: boolean;
  label?: string;
  unitBefore?: boolean;
  disabled?: boolean;
  optional?: boolean;
  frameColor?: string;
}) => {
  backgroundColor = backgroundColor || 'var(--background)';
  const containerRef = useRef<HTMLDivElement>(null);
  const isTiny = size === 'tiny';
  const noLabel = isTiny || !present(label);
  return (
    <div
      ref={containerRef}
      css={[
        LABELED_INPUT_CONTAINER_SIZE_STYLES[size],
        !!onClick && CLICKABLE_CONTAINER_STYLE,
        noLabel && LABELED_INPUT_CONTAINER_NO_TOP_LABEL_STYLE,
        { width: width || '100%' },
        disabled && INPUT_CONTAINER_STYLE_DISABLED,
      ]}
      onClick={onClick}
      title={title}
    >
      {!noLabel && (
        <div
          css={[
            COMMON_LABEL_STYLE,
            LABEL_SIZE_STYLES[size],
            LABEL_STYLES[style],
            LABEL_CONTENT_STYLE,
            frameColor && css`color: ${frameColor};`,
          ]}
          style={{ backgroundColor }}
        >
          <span>{label}</span>
          {optional && <span css={OPTIONAL_INDICATOR_STYLE}>(Optional)</span>}
        </div>
      )}
      <div
        css={[
          css`background-color: ${backgroundColor};`,
          INPUT_CONTAINER_STYLE,
          INPUT_CONTAINER_STYLES[style],
          INPUT_CONTAINER_SIZE_STYLES[size],
          style === 'taskCardField' && INPUT_CONTAINER_STYLE_TASK_CARD_FIELD_SIZE_OVERRIDE,
          isFocused && style !== 'taskCardField' && INPUT_CONTAINER_STYLE_FOCUSED_SIZES[size],
          unitBefore && (size === 'tightSmall' ? UNIT_BEFORE_STYLE_TIGHT_SMALL : UNIT_BEFORE_STYLE),
          frameColor && css`border: 1px solid ${frameColor};`,
          // after the frame color: while a task-card field is being edited, focus wins over an override's red
          isFocused && style === 'taskCardField' && INPUT_CONTAINER_STYLE_FOCUSED_TASK_CARD_FIELD,
        ]}
        data-input-container
      >
        {children}
      </div>
    </div>
  );
};
